import { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { Montadora } from '../types/montadora';

interface MontadoraSelectProps {
  montadoras: Montadora[];
  value: string;
  onChange: (value: string) => void;
  getLogoUrl: (logoPath: string | null | undefined) => string | null;
  disabled?: boolean;
  className?: string;
}

export function MontadoraSelect({ 
  montadoras, 
  value, 
  onChange, 
  getLogoUrl, 
  disabled = false,
  className = '' 
}: MontadoraSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const selectedMontadora = montadoras.find(m => m.nome === value);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          w-full px-3 py-2 bg-white border rounded-md shadow-sm text-base text-gray-700
          transition-colors duration-200 ease-in-out text-left flex items-center justify-between
          ${disabled ? 
            'bg-gray-50 text-gray-500 cursor-not-allowed' : 
            'border-gray-300 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-400'
          }
          focus:outline-none focus:ring-1 focus:ring-offset-0
        `}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {selectedMontadora && getLogoUrl(selectedMontadora.logo) && (
            <img 
              src={getLogoUrl(selectedMontadora.logo)!} 
              alt={selectedMontadora.nome} 
              className="w-6 h-6 object-contain flex-shrink-0"
              style={{ 
                imageRendering: 'crisp-edges',
                filter: 'contrast(1.1) saturate(1.1)',
              }}
            />
          )}
          <span className="truncate">
            {value || "Selecione uma montadora"}
          </span>
        </div>
        <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0" />
      </button>

      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-96 overflow-auto">
          <button
            type="button"
            onClick={() => {
              onChange('');
              setIsOpen(false);
            }}
            className="w-full px-3 py-2 text-left hover:bg-gray-100 text-gray-500 text-lg"
          >
            Selecione uma montadora
          </button>
          {montadoras.map((montadora) => (
            <button
              key={montadora.id}
              type="button"
              onClick={() => {
                onChange(montadora.nome);
                setIsOpen(false);
              }}
              className={`
                w-full px-3 py-2 text-left hover:bg-gray-100 flex items-center gap-2 text-lg
                ${value === montadora.nome ? 'bg-blue-50 text-blue-700' : 'text-gray-700'}
              `}
            >
              {getLogoUrl(montadora.logo) && (
                <img 
                  src={getLogoUrl(montadora.logo)!} 
                  alt={montadora.nome} 
                  className="w-9 h-9 object-contain flex-shrink-0"
                  style={{ 
                    imageRendering: 'crisp-edges',
                    filter: 'contrast(1.1) saturate(1.1)',
                  }}
                />
              )}
              <span className="truncate">{montadora.nome}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
} 